import {
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfWeek,
  endOfYear,
} from 'date-fns';
import { fromIsoDate, type IsoDateRange, type ScheduleMode, toIsoDate } from '@/utils/date-ranges';

export interface DateBucket {
  id: string;
  label: string;
  start: string;
  end: string;
}

interface DatedItem {
  date: string;
}

const WEEK_STARTS_MONDAY = { weekStartsOn: 1 } as const;

// One column per week (Monday to Sunday), month or year that the range touches, bounds inclusive.
export const buildDateBuckets = ({ from, to }: IsoDateRange, mode: ScheduleMode, locale: string): DateBucket[] => {
  if (to < from) {
    return [];
  }
  const interval = { start: fromIsoDate(from), end: fromIsoDate(to) };

  if (mode === 'week') {
    const dayFormat = new Intl.DateTimeFormat(locale);
    return eachWeekOfInterval(interval, WEEK_STARTS_MONDAY).map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, WEEK_STARTS_MONDAY);
      return {
        id: toIsoDate(weekStart),
        label: `${dayFormat.format(weekStart)} – ${dayFormat.format(weekEnd)}`,
        start: toIsoDate(weekStart),
        end: toIsoDate(weekEnd),
      };
    });
  }

  if (mode === 'month') {
    const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' });
    return eachMonthOfInterval(interval).map((monthStart) => ({
      id: toIsoDate(monthStart),
      label: monthFormat.format(monthStart),
      start: toIsoDate(monthStart),
      end: toIsoDate(endOfMonth(monthStart)),
    }));
  }

  return eachYearOfInterval(interval).map((yearStart) => ({
    id: toIsoDate(yearStart),
    label: String(yearStart.getFullYear()),
    start: toIsoDate(yearStart),
    end: toIsoDate(endOfYear(yearStart)),
  }));
};

export const itemsInBucket = <T extends DatedItem>(items: T[], bucket: DateBucket) =>
  items.filter((item) => item.date >= bucket.start && item.date <= bucket.end);
